import { useState } from 'react';
import { Box, Typography, Button, Checkbox, Stack, Autocomplete, TextField, ListItemText } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import DefaultLayout from 'src/layouts/DefaultLayout';
import GlobalHeader from 'src/components/GlobalHeader';
import { db } from '../instant';
import { queryAuthorizedUsersWithPermissions, UserPermission } from '../schema';
import { usePermissions } from 'src/hooks/usePermissions';
import PermissionDenied from 'src/components/PermissionDenied';
import ConfirmAdminChangeDialog from 'src/components/ConfirmAdminChangeDialog';
import ConfirmAllEventsDialog from 'src/components/ConfirmAllEventsDialog';
import DeleteUserDialog from 'src/components/DeleteUserDialog';
import { PERMISSION_KEYS, PERMISSION_LABELS } from 'src/constants/permissions';
import { BACKEND_BASE_URL } from '../constants';
import { processEventSelection } from 'src/utils/eventSelection';

type EventOption =
    | { id: string; name: string; startDate?: number | string | Date; endDate?: number | string | Date }
    | { id: 'all'; name: string };

function getEventOptionLabel(option: EventOption): string {
    if (option.id === 'all') return 'All Events';
    return option.name;
}

function getEventOptionSubtext(option: EventOption): string {
    if (option.id === 'all') return '';
    const e = option as { startDate?: number | string | Date; endDate?: number | string | Date };
    if (e.startDate) {
        const start = new Date(e.startDate);
        if (e.endDate) return `${format(start, 'M/d/yy')} – ${format(new Date(e.endDate), 'M/d/yy')}`;
        return format(start, 'M/d/yy');
    }
    if (e.endDate) return format(new Date(e.endDate), 'M/d/yy');
    return '';
}

function getSelectedEventsFromUser(allowedEventIds?: string): string[] {
    if (!allowedEventIds) return [];
    if (allowedEventIds === 'all') return ['all'];
    try {
        return JSON.parse(allowedEventIds);
    } catch {
        return [];
    }
}

export default function UserScreen() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { canEditUsers, isLoading: permissionsLoading } = usePermissions();
    const { data, isLoading, error } = queryAuthorizedUsersWithPermissions();
    const { user: authUser } = db.useAuth();
    const [optimisticPerms, setOptimisticPerms] = useState<Record<string, boolean> | null>(null);
    const [optimisticEventIds, setOptimisticEventIds] = useState<string | null | 'pending'>('pending');
    const [adminChangeDialog, setAdminChangeDialog] = useState<{
        open: boolean;
        isGranting: boolean;
    }>({ open: false, isGranting: false });
    const [allEventsDialog, setAllEventsDialog] = useState<{ open: boolean }>({ open: false });
    const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editingName, setEditingName] = useState<string | null>(null);
    const [savingName, setSavingName] = useState(false);

    const users = data?.$users || [];
    const events = data?.events || [];
    const user = userId ? users.find(u => u.id === userId) : null;

    if (!permissionsLoading && !canEditUsers) {
        return <PermissionDenied requiredPermission="Edit Users" />;
    }

    if (isLoading) {
        return (
            <DefaultLayout>
                <Box sx={{ p: 3 }}>Loading...</Box>
            </DefaultLayout>
        );
    }

    if (error) {
        return (
            <DefaultLayout>
                <Box sx={{ p: 3 }}>Error loading users: {String(error)}</Box>
            </DefaultLayout>
        );
    }

    if (!user) {
        return (
            <DefaultLayout>
                <Box sx={{ p: 3 }}>
                    <Typography>User not found.</Typography>
                    <Button component={Link} to="/users" sx={{ mt: 2 }}>
                        Back to Users
                    </Button>
                </Box>
            </DefaultLayout>
        );
    }

    const getPermissionValue = (key: keyof UserPermission): boolean => {
        if (optimisticPerms && key in optimisticPerms) return optimisticPerms[key];
        return (user[key as keyof UserPermission] as boolean) || false;
    };

    const getSelectedEvents = (): string[] => {
        if (optimisticEventIds === 'pending') {
            return getSelectedEventsFromUser(user.allowedEventIds);
        }
        if (optimisticEventIds === null) return [];
        if (optimisticEventIds === 'all') return ['all'];
        try {
            return JSON.parse(optimisticEventIds);
        } catch {
            return [];
        }
    };

    const handlePermissionChange = async (permissionKey: keyof UserPermission, value: boolean) => {
        if (permissionKey === 'admin') {
            setAdminChangeDialog({ open: true, isGranting: value });
            return;
        }
        await performPermissionChange(permissionKey, value);
    };

    const performPermissionChange = async (permissionKey: keyof UserPermission, value: boolean) => {
        try {
            const currentPerms =
                optimisticPerms ??
                (Object.fromEntries(PERMISSION_KEYS.map(k => [k, (user[k as keyof UserPermission] as boolean) || false])) as Record<
                    string,
                    boolean
                >);
            const newPerms = { ...currentPerms };

            if (permissionKey === 'admin') {
                PERMISSION_KEYS.forEach(k => {
                    newPerms[k] = value;
                });
            } else {
                newPerms[permissionKey] = value;
                const allNonAdminTrue = PERMISSION_KEYS.filter(k => k !== 'admin').every(k => newPerms[k] === true);
                if (allNonAdminTrue) newPerms.admin = true;
                else if (currentPerms.admin && !value) newPerms.admin = false;
            }

            setOptimisticPerms(newPerms);

            const userUpdates: Record<string, boolean> = { [permissionKey]: value };
            if (permissionKey === 'admin') {
                (PERMISSION_KEYS as readonly (keyof UserPermission)[]).forEach(k => {
                    if (k !== 'admin') userUpdates[k] = value;
                });
            } else {
                const allNonAdminTrue = PERMISSION_KEYS.filter(k => k !== 'admin').every(k =>
                    k === permissionKey ? value : (newPerms[k] ?? false)
                );
                if (allNonAdminTrue) userUpdates.admin = true;
                else if (currentPerms.admin && !value) userUpdates.admin = false;
            }

            await db.transact(db.tx.$users[user.id].update(userUpdates));
            setOptimisticPerms(null);
        } catch (err) {
            console.error('Failed to update permission:', err);
            setOptimisticPerms(null);
        }
    };

    const handleAdminChangeConfirm = async () => {
        await performPermissionChange('admin', adminChangeDialog.isGranting);
    };

    const performEventAccessChange = async (selectedEventIds: string[]) => {
        try {
            let eventIdsToStore: string | null;
            if (selectedEventIds.includes('all')) {
                eventIdsToStore = 'all';
            } else if (selectedEventIds.length === 0) {
                eventIdsToStore = null;
            } else {
                eventIdsToStore = JSON.stringify(selectedEventIds);
            }
            setOptimisticEventIds(eventIdsToStore);
            await db.transact(db.tx.$users[user.id].update({ allowedEventIds: eventIdsToStore }));
            setOptimisticEventIds('pending');
        } catch (err) {
            console.error('Failed to update event access:', err);
            setOptimisticEventIds('pending');
        }
    };

    const handleAllEventsConfirm = async () => {
        await performEventAccessChange(['all']);
    };

    const handleDeleteUserConfirm = async () => {
        if (!user) return;
        try {
            setDeleting(true);
            const token = authUser?.refresh_token;
            if (!token) throw new Error('Authentication token not available');
            const response = await fetch(`${BACKEND_BASE_URL}/delete-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token },
                body: JSON.stringify({ userId: user.id }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.error || 'Failed to delete user');
            setDeleteUserDialogOpen(false);
            navigate('/users');
        } catch (err) {
            console.error('Failed to delete user:', err);
        } finally {
            setDeleting(false);
        }
    };

    const selectedEvents = getSelectedEvents();
    const allEventsOption: EventOption = { id: 'all', name: 'All Events' };
    const eventOptions: EventOption[] = [allEventsOption, ...[...events].sort((a, b) => a.name.localeCompare(b.name))];
    const eventValue: EventOption[] = selectedEvents.includes('all')
        ? [allEventsOption]
        : events.filter(e => selectedEvents.includes(e.id));

    const handleEventOptionChange = (_: React.SyntheticEvent, newValue: EventOption[]) => {
        const ids = newValue.some(o => o.id === 'all') ? ['all'] : newValue.map(o => o.id);
        const result = processEventSelection(ids, getSelectedEvents());
        if (result.includes('all')) {
            setAllEventsDialog({ open: true });
            return;
        }
        performEventAccessChange(result);
    };

    const displayName = editingName !== null ? editingName : (user.name ?? '');
    const handleNameBlur = async () => {
        if (editingName === null) return;
        const trimmed = editingName.trim();
        if (trimmed === (user.name ?? '')) {
            setEditingName(null);
            return;
        }
        try {
            setSavingName(true);
            await db.transact(db.tx.$users[user.id].update({ name: trimmed || undefined }));
            setEditingName(null);
        } catch (err) {
            console.error('Failed to update name:', err);
        } finally {
            setSavingName(false);
        }
    };

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    alignItems: 'center',
                }}
            >
                <GlobalHeader title={user.name || user.email || 'User'} />
                <Box sx={{ p: 3, maxWidth: 600, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <Button component={Link} to="/users" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                        Back to Users
                    </Button>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            User
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            placeholder={user.email}
                            value={displayName}
                            onChange={e => setEditingName(e.target.value)}
                            onBlur={handleNameBlur}
                            disabled={savingName}
                            helperText={user.email}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Permissions
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Stack spacing={0.5}>
                                {PERMISSION_KEYS.map(key => (
                                    <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            size="small"
                                            checked={getPermissionValue(key as keyof UserPermission)}
                                            onChange={e => handlePermissionChange(key as keyof UserPermission, e.target.checked)}
                                        />
                                        <Typography variant="body2">{PERMISSION_LABELS[key]}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Events
                        </Typography>
                        <Autocomplete<EventOption, true>
                            multiple
                            disableCloseOnSelect
                            options={eventOptions}
                            value={eventValue}
                            onChange={handleEventOptionChange}
                            getOptionLabel={getEventOptionLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, option) => {
                                const subtext = getEventOptionSubtext(option);
                                return (
                                    <li
                                        {...props}
                                        key={option.id}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            props.onMouseDown?.(e);
                                        }}
                                    >
                                        <ListItemText
                                            primary={option.id === 'all' ? 'All Events' : option.name}
                                            secondary={subtext || undefined}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                    </li>
                                );
                            }}
                            renderInput={params => <TextField {...params} label="Event access" size="small" />}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteUserDialogOpen(true)}
                            disabled={deleting}
                        >
                            Delete user
                        </Button>
                    </Box>
                </Box>
            </Box>

            <ConfirmAdminChangeDialog
                open={adminChangeDialog.open}
                onClose={() => setAdminChangeDialog({ ...adminChangeDialog, open: false })}
                onConfirm={handleAdminChangeConfirm}
                userName={user.name || user.email || 'this user'}
                isGranting={adminChangeDialog.isGranting}
            />

            <ConfirmAllEventsDialog
                open={allEventsDialog.open}
                onClose={() => setAllEventsDialog({ open: false })}
                onConfirm={handleAllEventsConfirm}
                userName={user.name || user.email || 'this user'}
            />

            <DeleteUserDialog
                open={deleteUserDialogOpen}
                onClose={() => setDeleteUserDialogOpen(false)}
                onConfirm={handleDeleteUserConfirm}
                userName={user.name || user.email || 'this user'}
            />
        </DefaultLayout>
    );
}
